import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Always use relative /api for API calls — works on any deployed domain
const API = "/api";

const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Auth
export const createAuthSession = (sessionId) => api.post("/auth/session", { session_id: sessionId }).then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);

// Account deletion
export const getDeletionPreview = () => api.get("/account/deletion-preview").then((r) => r.data);
export const deleteAccount = (payload) => api.delete("/account", { data: payload }).then((r) => r.data);

// Moon Engine
export const fetchCurrentMoon = (tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC") =>
  api.get("/moon/current", { params: { tz } }).then((r) => r.data);
export const fetchLunarClimate = (isPremium = false, tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC") =>
  api.get(`/moon/lunar-climate`, { params: { is_premium: isPremium, tz } }).then((r) => r.data);

// Nakshatras
export const fetchNakshatras = () => api.get("/nakshatras").then((r) => Array.isArray(r.data) ? r.data : (r.data?.nakshatras ?? []));
export const fetchNakshatra = (slug) => api.get(`/nakshatras/${slug}`).then((r) => r.data);

// Natal Calculator
export const calculateNatal = (data) => api.post("/calculator/natal", data).then((r) => r.data);
export const calculateFullChart = (data) => api.post("/calculator/full-chart", data).then((r) => r.data);

// Admin
export const adminAuth = (passcode) => api.post("/admin/auth", { passcode }).then((r) => r.data);
export const updateNakshatra = (slug, data, passcode) =>
  api.put(`/admin/nakshatras/${slug}?x_admin_passcode=${encodeURIComponent(passcode)}`, data).then((r) => r.data);
export const uploadNakshatraImage = (slug, file, passcode) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("passcode", passcode);
  return api.post(`/admin/nakshatras/${slug}/image`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const exportAllData = (passcode) =>
  api.get(`/admin/export?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);
export const importData = (data, passcode) =>
  api.post(`/admin/import?x_admin_passcode=${encodeURIComponent(passcode)}`, data).then((r) => r.data);
export const seedDatabase = (passcode) =>
  api.post(`/admin/seed?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);

// Email
export const subscribeEmail = (data) => api.post("/email/subscribe", data).then((r) => r.data);
export const unsubscribeEmail = (email) => api.post("/email/unsubscribe", { email }).then((r) => r.data);

// Payments
export const createCheckout = (data) => api.post("/payments/checkout", data).then((r) => r.data);
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`).then((r) => r.data);

// Premium
export const checkPremium = (email) => api.get(`/premium/check?email=${encodeURIComponent(email)}`).then((r) => r.data);
export const fetchPremiumClimate = (email, tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC") =>
  api.get(`/moon/lunar-climate/premium`, { params: { email, tz } }).then((r) => r.data);

// User Profile
export const getUserProfile = () => api.get("/user/profile").then((r) => r.data);
export const saveUserNatal = (data) => api.post("/user/natal/save", data).then((r) => r.data);

// Gated Content
export const getDetailedPadas = (slug) => api.get(`/nakshatras/${slug}/padas/detailed`).then((r) => r.data);

// Admin Premium Testing
export const adminGrantPremium = (email, passcode) =>
  api.post(`/admin/premium/grant?x_admin_passcode=${encodeURIComponent(passcode)}`, { email }).then((r) => r.data);
export const adminRevokePremium = (email, passcode) =>
  api.post(`/admin/premium/revoke?x_admin_passcode=${encodeURIComponent(passcode)}`, { email }).then((r) => r.data);
export const adminListPremiumUsers = (passcode) =>
  api.get(`/admin/premium/users?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);
export const adminResetMonthlyReports = (passcode) =>
  api.post(`/admin/premium/reset-reports?x_admin_passcode=${encodeURIComponent(passcode)}`, {}).then((r) => r.data);
export const adminListPayments = (passcode) =>
  api.get(`/admin/payments?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);

// Weekly digest admin controls
export const adminDigestPreview = (passcode) =>
  api.post(`/admin/digest/preview?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);
export const adminDigestSend = (passcode) =>
  api.post(`/admin/digest/send?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);
export const adminDigestSendTest = (passcode, testEmail) =>
  api.post(`/admin/digest/send?x_admin_passcode=${encodeURIComponent(passcode)}&test_email=${encodeURIComponent(testEmail)}`).then((r) => r.data);
export const adminDigestLogs = (passcode) =>
  api.get(`/admin/digest/logs?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);

// Compatibility
export const calculateCompatibility = (data) => api.post("/calculator/compatibility", data).then((r) => r.data);

// Dasha Calculator (Mahadasha/Antardasha)
export const calculateDasha = (data) => api.post("/calculator/dasha", data).then((r) => r.data);

// Dasha Report PDF
export const generateDashaReport = (data) =>
  api.post("/report/dasha/generate", data, { responseType: "blob" }).then((r) => r.data);

// Compatibility Report PDF
export const generateCompatibilityReport = (data) =>
  api.post("/report/compatibility/generate", data, { responseType: "blob" }).then((r) => r.data);

// Natal Report PDF
export const generateNatalReport = (data) =>
  api.post("/report/natal/generate", data, { responseType: "blob" }).then((r) => r.data);

// Karmic Access Report
export const getKarmicUsage = () => api.get("/karmic/usage").then((r) => r.data);
export const getKarmicPreview = () => api.get("/karmic/preview").then((r) => r.data);
export const generateKarmicReport = (data) => api.post("/karmic/generate", data).then((r) => r.data);
export const listKarmicReports = () => api.get("/karmic/reports").then((r) => r.data);
export const getKarmicReport = (id) => api.get(`/karmic/reports/${id}`).then((r) => r.data);
export const downloadKarmicPdf = (id) =>
  api.get(`/karmic/reports/${id}/pdf`, { responseType: "blob" }).then((r) => r.data);

// Admin subscribers
export const fetchSubscribers = (passcode) =>
  api.get(`/admin/subscribers?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);

// Daily Chart Activation
export const getDailyActivation = (tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC") =>
  api.get("/user/daily-activation", { params: { tz } }).then((r) => r.data);
export const updateUserBirthData = (data) => api.post("/user/birth-data", data).then((r) => r.data);
export const getUserBirthData = () => api.get("/user/birth-data").then((r) => r.data);
export const getPersonalHoroscope = (tz, date) =>
  api.get("/horoscope/personal", { params: { tz, date } }).then((r) => r.data);

// Knowledge Base
export const fetchKBCategories = () => api.get("/kb/categories").then((r) => r.data);
export const fetchKBCategory = (slug) => api.get(`/kb/categories/${slug}`).then((r) => r.data);
export const fetchKBArticle = (slug) => api.get(`/kb/articles/${slug}`).then((r) => r.data);
export const searchKB = (q) => api.get(`/kb/search?q=${encodeURIComponent(q)}`).then((r) => r.data);

// Admin KB
export const adminCreateKBArticle = (data, passcode) =>
  api.post(`/admin/kb/articles?x_admin_passcode=${encodeURIComponent(passcode)}`, data).then((r) => r.data);
export const adminUpdateKBArticle = (slug, data, passcode) =>
  api.put(`/admin/kb/articles/${slug}?x_admin_passcode=${encodeURIComponent(passcode)}`, data).then((r) => r.data);
export const adminDeleteKBArticle = (slug, passcode) =>
  api.delete(`/admin/kb/articles/${slug}?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);
export const adminCreateKBCategory = (data, passcode) =>
  api.post(`/admin/kb/categories?x_admin_passcode=${encodeURIComponent(passcode)}`, data).then((r) => r.data);
export const adminUpdateKBCategory = (slug, data, passcode) =>
  api.put(`/admin/kb/categories/${slug}?x_admin_passcode=${encodeURIComponent(passcode)}`, data).then((r) => r.data);
export const adminDeleteKBCategory = (slug, passcode) =>
  api.delete(`/admin/kb/categories/${slug}?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);
export const adminSeedKB = (passcode) =>
  api.post(`/admin/kb/seed?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);

// Analytics
export const trackAnalyticsEvents = (events) => api.post("/analytics/track", { events }).then((r) => r.data).catch(() => {});
export const trackAnalyticsSession = (data) => api.post("/analytics/session", data).then((r) => r.data).catch(() => {});
export const adminAnalyticsLive = (passcode) =>
  api.get(`/admin/analytics/live?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);
export const adminAnalyticsMetrics = (passcode, range = "today") =>
  api.get(`/admin/analytics/metrics?x_admin_passcode=${encodeURIComponent(passcode)}&range=${range}`).then((r) => r.data);
export const adminAnalyticsTopPages = (passcode, range = "today") =>
  api.get(`/admin/analytics/top-pages?x_admin_passcode=${encodeURIComponent(passcode)}&range=${range}`).then((r) => r.data);
export const adminAnalyticsFunnel = (passcode, range = "today") =>
  api.get(`/admin/analytics/funnel?x_admin_passcode=${encodeURIComponent(passcode)}&range=${range}`).then((r) => r.data);
export const adminAnalyticsEvents = (passcode, params = {}) => {
  const qs = new URLSearchParams({ x_admin_passcode: passcode, ...params }).toString();
  return api.get(`/admin/analytics/events?${qs}`).then((r) => r.data);
};
export const adminAnalyticsEventTypes = (passcode) =>
  api.get(`/admin/analytics/event-types?x_admin_passcode=${encodeURIComponent(passcode)}`).then((r) => r.data);
export const adminAnalyticsExport = (passcode, range, exportType) =>
  api.get(`/admin/analytics/export?x_admin_passcode=${encodeURIComponent(passcode)}&range=${range}&export_type=${exportType}`, { responseType: "blob" }).then((r) => r.data);

// Report Usage (Free reports with monthly limit)
export const getReportUsage = () => api.get("/reports/usage").then((r) => r.data);
export const purchaseAdditionalReport = (reportType) => 
  api.post("/payments/additional-report", { 
    report_type: reportType, 
    origin_url: window.location.origin 
  }).then((r) => r.data);

// Astrological Events
export const fetchUpcomingEvents = (limit = 6, days = 90, category = null) => {
  const params = new URLSearchParams({ limit, days });
  if (category) params.append("category", category);
  return api.get(`/events/upcoming?${params}`).then((r) => r.data);
};
export const fetchAllEvents = (days = 90, category = null) => {
  const params = new URLSearchParams({ days });
  if (category) params.append("category", category);
  return api.get(`/events/all?${params}`).then((r) => r.data);
};

// Email Digest Subscription
export const subscribeToDigest = (email, options = {}) => 
  api.post("/email/subscribe", { 
    email, 
    digest_type: "weekly",
    first_name: options.firstName || "",
    last_name: options.lastName || "",
    timezone_str: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    ...options 
  }).then((r) => r.data);

export const unsubscribeFromDigest = (email) => 
  api.post("/email/unsubscribe", { email }).then((r) => r.data);

export { API, BACKEND_URL };
export default api;
